import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { configureAuth } from '@acitra/api-client';
import { loadConfig } from '../config';
import { useAuthStore } from '../stores/auth-store';

/**
 * AuthCallback handles the OAuth redirect from Cognito.
 *
 * The flow:
 * 1. Configure Amplify with OAuth settings (MUST happen first)
 * 2. Listen for Hub 'signInWithRedirect' event (Amplify processes the URL automatically)
 * 3. Once the event fires, call getCurrentUser() to get user info
 * 4. Redirect to appropriate page based on tenant state
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const { fetchUserTenants, tenants } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(true);
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const [authCompleted, setAuthCompleted] = useState(false);
  const configuredRef = useRef(false);

  // Step 1: Configure Amplify FIRST, then let it process the OAuth callback
  useEffect(() => {
    const configureAndListen = async () => {
      try {
        // Check for error in URL params first
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        console.log('[AuthCallback] URL:', window.location.href);
        console.log('[AuthCallback] URL params:', Object.fromEntries(urlParams.entries()));

        if (errorParam) {
          setCallbackError(errorDescription || errorParam);
          setIsProcessing(false);
          return;
        }

        // Only configure once
        if (configuredRef.current) {
          return;
        }
        configuredRef.current = true;

        // Load and configure auth
        console.log('[AuthCallback] Loading config...');
        const config = await loadConfig();
        console.log('[AuthCallback] Config loaded:', {
          hasUserPoolId: !!config.cognitoUserPoolId,
          hasClientId: !!config.cognitoClientId,
          hasDomain: !!config.cognitoDomain,
          domain: config.cognitoDomain,
        });

        if (!config.cognitoUserPoolId || !config.cognitoClientId) {
          setCallbackError('Auth configuration not available');
          setIsProcessing(false);
          return;
        }

        const currentUrl = window.location.origin;
        console.log('[AuthCallback] Configuring Amplify with OAuth...');

        configureAuth({
          userPoolId: config.cognitoUserPoolId,
          userPoolClientId: config.cognitoClientId,
          oauth: config.cognitoDomain
            ? {
                domain: config.cognitoDomain,
                scopes: ['openid', 'email', 'profile'],
                redirectSignIn: [`${currentUrl}/auth/callback`],
                redirectSignOut: [currentUrl],
                responseType: 'code',
              }
            : undefined,
        });

        console.log('[AuthCallback] Amplify configured, waiting for OAuth processing...');

        // After configuring, try to get the current user
        // Amplify should process the OAuth code from the URL automatically
        // Wait a bit to let Amplify process the URL
        await new Promise((resolve) => setTimeout(resolve, 500));

        console.log('[AuthCallback] Attempting to get current user...');
        try {
          // Try to get auth session - this should trigger OAuth code exchange
          const session = await fetchAuthSession();
          console.log('[AuthCallback] Auth session:', {
            hasTokens: !!session.tokens,
            hasAccessToken: !!session.tokens?.accessToken,
          });

          if (session.tokens?.accessToken) {
            // Check email verification from ID token (required by Intuit)
            const emailVerified = session.tokens?.idToken?.payload?.['email_verified'];
            console.log('[AuthCallback] Email verified:', emailVerified);

            if (emailVerified === false) {
              // Store error for Login page to display after redirect
              console.log('[AuthCallback] Email not verified, storing error and signing out...');
              sessionStorage.setItem('auth_error_type', 'intuit_email_not_verified');
              await signOut();
              // signOut will redirect to login page, the stored error will be displayed there
              return;
            }

            const user = await getCurrentUser();
            console.log('[AuthCallback] Got user:', user);
            setAuthCompleted(true);
            setIsProcessing(false);
          } else {
            console.log('[AuthCallback] No tokens in session');
            setCallbackError('Failed to obtain authentication tokens');
            setIsProcessing(false);
          }
        } catch (authError) {
          console.error('[AuthCallback] Auth error:', authError);
          // If user isn't authenticated, show error
          setCallbackError(
            authError instanceof Error ? authError.message : 'Authentication failed'
          );
          setIsProcessing(false);
        }
      } catch (err) {
        console.error('[AuthCallback] Configuration error:', err);
        setCallbackError(err instanceof Error ? err.message : 'Authentication failed');
        setIsProcessing(false);
      }
    };

    // Set up Hub listener for auth events
    const hubListener = Hub.listen(
      'auth',
      ({ payload }: { payload: { event: string; data?: unknown } }) => {
        console.log('[AuthCallback] Hub event:', payload.event, payload);

        switch (payload.event) {
          case 'signInWithRedirect':
            console.log('[AuthCallback] signInWithRedirect completed!');
            // OAuth flow completed successfully, check email verification then get user
            fetchAuthSession()
              .then(async (session) => {
                const emailVerified = session.tokens?.idToken?.payload?.['email_verified'];
                console.log('[AuthCallback] Email verified (from Hub):', emailVerified);

                if (emailVerified === false) {
                  // Store error for Login page to display after redirect
                  console.log(
                    '[AuthCallback] Email not verified, storing error and signing out...'
                  );
                  sessionStorage.setItem('auth_error_type', 'intuit_email_not_verified');
                  await signOut();
                  // signOut will redirect to login page, the stored error will be displayed there
                  return;
                }

                const user = await getCurrentUser();
                console.log('[AuthCallback] User from signInWithRedirect:', user);
                setAuthCompleted(true);
                setIsProcessing(false);
              })
              .catch((err: unknown) => {
                console.error('[AuthCallback] Failed to get user after redirect:', err);
                setCallbackError('Failed to get user information');
                setIsProcessing(false);
              });
            break;

          case 'signInWithRedirect_failure':
            console.error('[AuthCallback] signInWithRedirect failed:', payload.data);
            setCallbackError((payload.data as Error)?.message || 'OAuth sign-in failed');
            setIsProcessing(false);
            break;

          case 'customOAuthState':
            console.log('[AuthCallback] Custom OAuth state:', payload.data);
            break;
        }
      }
    );

    configureAndListen();

    return () => hubListener();
  }, []);

  // Step 2: Once auth is completed, fetch tenants
  useEffect(() => {
    if (authCompleted) {
      console.log('[AuthCallback] Auth completed, fetching tenants...');
      fetchUserTenants().then(() => {
        console.log('[AuthCallback] Tenants fetched, ready to redirect');
      });
    }
  }, [authCompleted, fetchUserTenants]);

  // Step 3: Redirect after successful auth and tenant fetch
  useEffect(() => {
    // Don't redirect if there's an error (e.g., email not verified)
    if (authCompleted && !isProcessing && !callbackError) {
      console.log('[AuthCallback] Ready to redirect, tenants:', tenants.length);
      // Small delay to ensure state is fully updated
      const timer = setTimeout(() => {
        // Check if there's a pending invitation to return to
        const invitationToken = sessionStorage.getItem('invitation_return_token');
        if (invitationToken) {
          sessionStorage.removeItem('invitation_return_token');
          navigate(`/invite/${invitationToken}`, { replace: true });
          return;
        }

        if (tenants.length === 1) {
          // Single tenant - go directly to app
          navigate('/', { replace: true });
        } else {
          // No tenants or multiple tenants - go to company selection
          navigate('/companies', { replace: true });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authCompleted, isProcessing, tenants, navigate, callbackError]);

  if (callbackError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">Authentication Failed</h2>
            <p className="mt-2 text-sm text-red-600">{callbackError}</p>
          </div>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 text-center">
        <div>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">Completing Sign In</h2>
          <p className="mt-2 text-sm text-gray-600">
            Please wait while we complete your sign in...
          </p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    </div>
  );
}
